import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import html2canvas from "html2canvas";
import { 
  Share2, 
  Download, 
  Copy, 
  MessageSquare, 
  Mail, 
  Twitter, 
  Facebook, 
  Instagram,
  Link2,
  Smartphone,
  Crown,
  Scissors,
  Sparkles,
  ExternalLink,
  Palette,
  Eye,
  Shield,
  TrendingUp,
  Heart,
  Loader2,
  Check,
  Image as ImageIcon
} from "lucide-react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import SocialMediaCards from "./SocialMediaCards";

// Professional card images
const CARD_IMAGES = {
  stylist: "https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/63d58951e5ab7e31ae4c087a16e2dd8d29cd67a8c02b18e2b7a2b5eca234ad44.png",
  barber: "https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/aa29a7a60dd7530a808edfa970ef127c15e34d31eafd91409f265f4e14c47ee7.png",
  beautician: "https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/32e8be5d6280edba211d9172f3ca23a838a731bc26ff9efe64311cbc1fff5878.png",
  soldier: "https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/740fea886976a48a04363251ce14b0bb1b94d1de5fdf9255dd1492cc33ac8e89.png",
  ceo: "https://customer-assets.emergentagent.com/job_2f9006eb-b332-428d-86e9-eb851b9d0232/artifacts/dd8z5zd8_GeorgeFounder%20Headshot.jpeg"
};

/**
 * Digital Marketing Toolkit - Full Share Functionality
 * 
 * 5 Professional Social Cards:
 * - Stylist, Beautician, Barber, Soldier Recruitment, CEO Message
 * 
 * Real sharing via:
 * - Download image (html2canvas)
 * - SMS with auto-attached image
 * - Email with embedded image
 * - Social media with Web Share API
 */
const DigitalMarketingToolkit = ({ token, userProfile }) => {
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState("stylist");
  const [shareStats, setShareStats] = useState({ clicks: 0, shares: 0 });
  const [generating, setGenerating] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [generatedBlob, setGeneratedBlob] = useState(null);
  const [spotsRemaining] = useState(38);
  const cardRef = useRef(null);

  // Fetch share stats on mount
  useEffect(() => {
    if (token) {
      fetchShareStats();
    }
  }, [token]);

  // Generate image when template changes
  useEffect(() => {
    if (cardRef.current) {
      // Small delay to ensure images are loaded
      const timer = setTimeout(() => {
        generateCardImage();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedTemplate]);

  const fetchShareStats = async () => {
    try {
      const res = await axios.get(`${API}/marketing/share-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShareStats({
        shares: res.data.total_shares || 0,
        clicks: res.data.estimated_clicks || 0
      });
    } catch (error) {
      console.error("Failed to fetch share stats:", error);
    }
  };

  // Dynamic data from user profile
  const userData = {
    name: userProfile?.business_name || userProfile?.name || user?.name || "Your Name",
    shopName: userProfile?.shop_name || userProfile?.business_name || "MyHairChair Professional",
    profileLink: `${window.location.origin}/book/${user?.user_id || "profile"}?source=crm_card`,
    soldierLink: `${window.location.origin}/soldiers?ref=${user?.user_id || "user"}&source=crm_card`,
    instagramHandle: userProfile?.instagram_handle || user?.instagram_handle || "",
    role: user?.role || "stylist"
  };

  // Card Templates - 5 Templates including CEO
  const templates = {
    stylist: {
      id: "stylist",
      name: "Hair Stylist",
      subtitle: "For Hair Stylists",
      headline: "Book Your Next Session",
      body: "Skip the app fees and pay me direct via Zelle, PayPal, or Cash App. It's easier for both of us!",
      cta: "Book Now",
      ctaLink: userData.profileLink,
      gradient: "from-amber-500 via-orange-500 to-rose-500",
      bgGradient: "from-stone-900 via-stone-800 to-stone-900",
      icon: Scissors,
      accentColor: "amber",
      image: CARD_IMAGES.stylist,
      smsMessage: `Yo! I'm moving my booking to MyHairChair. It's easier for both of us—you can skip the app fees and pay me direct via Zelle, PayPal, or Cash App. Book your next spot here: ${userData.profileLink}`,
      emailSubject: `Book Your Next Appointment with ${userData.name}`,
      hashtags: ["#CityStylist", "#MyHairChair", "#BookDirect", "#NoFees", "#HairGoals"]
    },
    beautician: {
      id: "beautician",
      name: "Beautician",
      subtitle: "For Estheticians & Beauty Pros",
      headline: "Your Beauty Session Awaits",
      body: "Skip the convenience fees. I accept Zelle, PayPal, and Cash App for a seamless experience.",
      cta: "Book Your Glow",
      ctaLink: userData.profileLink,
      gradient: "from-pink-500 via-rose-500 to-purple-500",
      bgGradient: "from-stone-900 via-rose-950 to-stone-900",
      icon: Heart,
      accentColor: "pink",
      image: CARD_IMAGES.beautician,
      smsMessage: `Hey babe! I'm now booking through MyHairChair—no more extra fees! Pay me direct via Zelle, PayPal, or Cash App. Book your next glow up here: ${userData.profileLink}`,
      emailSubject: `Book Your Beauty Session with ${userData.name}`,
      hashtags: ["#BeautyPro", "#MyHairChair", "#BookDirect", "#Esthetician", "#GlowUp"]
    },
    barber: {
      id: "barber",
      name: "Barber",
      subtitle: "For Barbers",
      headline: "Your Barber is on MyHairChair!",
      body: "Secure your spot in the chair. Direct payments via Zelle, PayPal, or Cash App accepted. No cap.",
      cta: "Book Your Cut",
      ctaLink: userData.profileLink,
      gradient: "from-blue-500 via-indigo-500 to-purple-500",
      bgGradient: "from-slate-900 via-slate-800 to-slate-900",
      icon: Crown,
      accentColor: "blue",
      image: CARD_IMAGES.barber,
      smsMessage: `Yo! I'm moving my booking to MyHairChair. It's easier for both of us—you can skip the app fees and pay me direct via Zelle, PayPal, or Cash App. Book your next spot here: ${userData.profileLink}`,
      emailSubject: `Your Barber ${userData.name} is Now on MyHairChair`,
      hashtags: ["#CityBarber", "#MyHairChair", "#GetCut", "#DirectPay", "#FreshCut"]
    },
    soldier: {
      id: "soldier",
      name: "Soldier Recruitment",
      subtitle: "Founding 50 EXCLUSIVE",
      headline: "Join the Founding 50",
      body: `Earn lifetime residuals and automate your shop's rent. We are 2026's biggest move. Only ${spotsRemaining} spots left.`,
      cta: "Apply Now",
      ctaLink: userData.soldierLink,
      gradient: "from-emerald-500 via-teal-500 to-cyan-500",
      bgGradient: "from-black via-emerald-950 to-black",
      icon: Shield,
      accentColor: "emerald",
      image: CARD_IMAGES.soldier,
      smsMessage: `I'm looking for the top 50 shop owners to join the MyHairChair inner circle. Earn lifetime residuals and automate your shop's rent. We are 2026's biggest move. Apply here: ${userData.soldierLink} — Only ${spotsRemaining} spots left.`,
      emailSubject: "EXCLUSIVE: Join the MyHairChair Founding 50 - Lifetime Residuals",
      hashtags: ["#HairChairSoldier", "#Founding50", "#LifetimeResiduals", "#ShopOwner", "#2026Move"],
      exclusive: true
    },
    ceo: {
      id: "ceo",
      name: "CEO Message",
      subtitle: "From the Founder",
      headline: "A Message from Our CEO",
      body: "We're building something special—BY Barbers and Stylists, FOR Barbers and Stylists. Join the movement that's changing the game.",
      cta: "Join the Movement",
      ctaLink: userData.soldierLink,
      gradient: "from-amber-400 via-yellow-500 to-orange-500",
      bgGradient: "from-black via-amber-950 to-black",
      icon: Crown,
      accentColor: "yellow",
      image: CARD_IMAGES.ceo,
      smsMessage: `🪑 A message from MyHairChair's CEO: We're on a nationwide hunt for the best salon owners, stylists, and barbers to join our founding team. Earn lifetime residuals. This is YOUR shot. ${userData.soldierLink}`,
      emailSubject: "A Personal Message from MyHairChair's CEO",
      hashtags: ["#MyHairChair", "#CEOMessage", "#JoinTheMovement", "#FoundingTeam", "#LifetimeResiduals"],
      exclusive: true,
      isCeoCard: true
    }
  };

  const currentTemplate = templates[selectedTemplate];

  // Generate card image using html2canvas
  const generateCardImage = useCallback(async () => {
    if (!cardRef.current) return null;
    
    setGenerating(true);
    try {
      // Wait for images to load
      const images = cardRef.current.getElementsByTagName('img');
      await Promise.all(
        Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
        })
      );

      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: '#000000',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      // Convert to blob for sharing
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
      const url = canvas.toDataURL('image/png');
      
      setGeneratedImageUrl(url);
      setGeneratedBlob(blob);
      
      return { url, blob, canvas };
    } catch (error) {
      console.error("Failed to generate card image:", error);
      return null;
    } finally {
      setGenerating(false);
    }
  }, [selectedTemplate]);

  // Track share/click analytics
  const trackShare = async (method) => {
    try {
      await axios.post(`${API}/marketing/track-share`, {
        template_id: selectedTemplate,
        share_method: method,
        utm_source: "crm_card"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShareStats(prev => ({ ...prev, shares: prev.shares + 1 }));
    } catch (error) {
      console.error("Failed to track share:", error);
    }
  };

  // Download card as image
  const downloadCard = async () => {
    setGenerating(true);
    try {
      const result = await generateCardImage();
      if (result?.url) {
        const link = document.createElement("a");
        link.download = `myhairchair_${selectedTemplate}_card.png`;
        link.href = result.url;
        link.click();
        toast.success("Card downloaded! Ready to share.");
        trackShare("download");
      }
    } catch (error) {
      toast.error("Failed to download card");
    } finally {
      setGenerating(false);
    }
  };

  // Share via Web Share API with image file
  const shareWithImage = async (platform) => {
    setGenerating(true);
    try {
      const result = await generateCardImage();
      
      if (!result?.blob) {
        toast.error("Failed to generate image");
        return;
      }

      // Create file from blob
      const file = new File([result.blob], `myhairchair_${selectedTemplate}.png`, { type: 'image/png' });
      
      const shareData = {
        title: currentTemplate.headline,
        text: `${currentTemplate.body}\n\n${currentTemplate.hashtags.join(" ")}\n\n${currentTemplate.ctaLink}`,
        files: [file]
      };

      // Check if Web Share API supports files
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success("Shared successfully!");
        trackShare(platform);
      } else if (navigator.share) {
        // Fallback: share without file
        await navigator.share({
          title: shareData.title,
          text: shareData.text,
          url: currentTemplate.ctaLink
        });
        toast.success("Shared! (Download image separately for visual)");
        trackShare(platform);
      } else {
        // Fallback: download image and open share URL
        downloadCard();
        if (platform === 'instagram') {
          toast.info("Image downloaded! Open Instagram and share from your gallery.");
        } else {
          const shareUrls = {
            twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData.text)}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentTemplate.ctaLink)}`
          };
          if (shareUrls[platform]) {
            window.open(shareUrls[platform], "_blank", "width=600,height=400");
          }
        }
        trackShare(platform);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Share failed:", error);
        toast.error("Share failed. Try downloading instead.");
      }
    } finally {
      setGenerating(false);
    }
  };

  // Share via SMS with image
  const shareViaSMS = async () => {
    setGenerating(true);
    try {
      // First download the image
      const result = await generateCardImage();
      if (result?.url) {
        // Download image to user's device
        const link = document.createElement("a");
        link.download = `myhairchair_${selectedTemplate}_card.png`;
        link.href = result.url;
        link.click();
      }
      
      // Open SMS with pre-filled text
      const smsBody = encodeURIComponent(currentTemplate.smsMessage);
      
      // Small delay to let download complete
      setTimeout(() => {
        window.location.href = `sms:?body=${smsBody}`;
        toast.success("Image downloaded + SMS opened! Attach the image from your downloads.");
        trackShare("sms");
      }, 500);
    } catch (error) {
      toast.error("Failed to prepare SMS");
    } finally {
      setGenerating(false);
    }
  };

  // Share via Email with image
  const shareViaEmail = async () => {
    setGenerating(true);
    try {
      // Download image first
      const result = await generateCardImage();
      if (result?.url) {
        const link = document.createElement("a");
        link.download = `myhairchair_${selectedTemplate}_card.png`;
        link.href = result.url;
        link.click();
      }
      
      const subject = encodeURIComponent(currentTemplate.emailSubject);
      const body = encodeURIComponent(
        `${currentTemplate.headline}\n\n` +
        `${currentTemplate.body}\n\n` +
        `👉 ${currentTemplate.ctaLink}\n\n` +
        `${currentTemplate.hashtags.join(" ")}\n\n` +
        `[Attach the downloaded image to this email]`
      );
      
      setTimeout(() => {
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
        toast.success("Image downloaded + Email opened! Attach the image from your downloads.");
        trackShare("email");
      }, 500);
    } catch (error) {
      toast.error("Failed to prepare email");
    } finally {
      setGenerating(false);
    }
  };

  // Copy link to clipboard
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(currentTemplate.ctaLink);
      toast.success("Link copied to clipboard!");
      trackShare("copy");
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Palette className="w-6 h-6 text-amber-400" />
            Digital Marketing Toolkit
          </h2>
          <p className="text-stone-400 mt-1">Generate &amp; share professional Social Cards instantly</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            <Check className="w-3 h-3 mr-1" />
            Auto-Share Ready
          </Badge>
          <Badge className="bg-pink-500/20 text-pink-400 border-pink-500/30">
            <Instagram className="w-3 h-3 mr-1" />
            IG Ready
          </Badge>
        </div>
      </div>

      {/* CEO Message Banner for CEO or Soldier template */}
      {(selectedTemplate === "soldier" || selectedTemplate === "ceo") && (
        <Card className="bg-gradient-to-r from-amber-900/50 via-yellow-900/30 to-orange-900/50 border-amber-500/30 overflow-hidden">
          <CardContent className="p-4 relative">
            <div className="absolute top-2 right-2">
              <Badge className="bg-red-500 text-white animate-pulse">
                🔥 {spotsRemaining} SPOTS LEFT
              </Badge>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Crown className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-2">
                  Message from the CEO
                </Badge>
                <p className="text-white font-medium mb-2">
                  We&apos;re on a nationwide hunt for the Best Salon Business Owners, Stylists, and Barbers!
                </p>
                <p className="text-stone-300 text-sm">
                  Become a HairChair Soldier — tell your followers, share, and earn <span className="text-amber-400 font-semibold">lifetime residuals</span>. 
                  This is a once-in-a-lifetime opportunity. You will earn from your lineage of followers.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Selector - 5 Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {Object.values(templates).map((template) => {
          const Icon = template.icon;
          const isSelected = selectedTemplate === template.id;
          return (
            <Card
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={`cursor-pointer transition-all duration-300 overflow-hidden ${
                isSelected 
                  ? `ring-2 ring-${template.accentColor}-500 bg-stone-800` 
                  : "bg-stone-800/50 border-stone-700 hover:border-stone-600 hover:scale-[1.02]"
              }`}
            >
              <div className="relative h-20 overflow-hidden">
                <img 
                  src={template.image} 
                  alt={template.name}
                  className="w-full h-full object-cover object-top"
                  crossOrigin="anonymous"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                {template.exclusive && (
                  <Badge className="absolute top-1 right-1 bg-amber-500/90 text-black text-[10px] px-1.5 py-0.5">
                    {template.isCeoCard ? "CEO" : "VIP"}
                  </Badge>
                )}
              </div>
              <CardContent className="p-2">
                <div className="flex items-center gap-1.5">
                  <div className={`w-5 h-5 bg-gradient-to-br ${template.gradient} rounded flex items-center justify-center`}>
                    <Icon className="w-3 h-3 text-white" />
                  </div>
                  <span className="font-semibold text-white text-xs truncate">{template.name}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Card Preview */}
        <Card className="bg-stone-800/50 border-stone-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-amber-400" />
              Card Preview
              {generating && <Loader2 className="w-4 h-4 animate-spin text-amber-400" />}
            </CardTitle>
            <CardDescription className="text-stone-400">
              This image will be shared automatically
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* The Social Card - This gets converted to image */}
            <div
              ref={cardRef}
              className="relative overflow-hidden rounded-2xl bg-black aspect-[4/5] max-w-sm mx-auto shadow-2xl"
              style={{ backgroundColor: '#000000' }}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img 
                  src={currentTemplate.image}
                  alt={currentTemplate.name}
                  className="w-full h-full object-cover"
                  crossOrigin="anonymous"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${currentTemplate.bgGradient} opacity-70`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              </div>

              {/* Content Overlay */}
              <div className="relative z-10 h-full flex flex-col p-5">
                {/* Top Bar */}
                <div className="flex items-center justify-between mb-auto">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 bg-gradient-to-br ${currentTemplate.gradient} rounded-lg flex items-center justify-center`}>
                      <Scissors className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white/90 text-sm font-semibold">MyHairChair</span>
                  </div>
                  {currentTemplate.exclusive && (
                    <Badge className="bg-amber-500/90 text-black text-xs font-bold px-2">
                      {currentTemplate.isCeoCard ? "CEO MESSAGE" : "FOUNDING 50"}
                    </Badge>
                  )}
                </div>

                {/* Main Content */}
                <div className="mt-auto space-y-3">
                  <h3 className={`text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${currentTemplate.gradient} leading-tight`}>
                    {currentTemplate.headline}
                  </h3>
                  
                  <p className="text-white/90 text-sm leading-relaxed">
                    {currentTemplate.body}
                  </p>
                  
                  {/* User Info Card */}
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-3 border border-white/10">
                    <p className="text-white font-bold">{userData.name}</p>
                    <p className="text-white/70 text-sm">{userData.shopName}</p>
                    {userData.instagramHandle && (
                      <p className="text-pink-400 text-xs mt-1">@{userData.instagramHandle}</p>
                    )}
                  </div>

                  {/* Hashtags */}
                  <div className="flex flex-wrap gap-1.5">
                    {currentTemplate.hashtags.slice(0, 4).map((tag, idx) => (
                      <span key={idx} className="text-xs text-white/50 bg-white/5 px-2 py-0.5 rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* CTA & QR */}
                  <div className="flex items-end justify-between pt-2">
                    <div className={`bg-gradient-to-r ${currentTemplate.gradient} text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-2`}>
                      {currentTemplate.cta}
                      <ExternalLink className="w-4 h-4" />
                    </div>
                    
                    <div className="bg-white p-2 rounded-lg shadow-lg">
                      <QRCode
                        value={currentTemplate.ctaLink}
                        size={56}
                        level="M"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Share Options */}
        <div className="space-y-4">
          {/* Main Share Actions */}
          <Card className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2 text-lg">
                <Share2 className="w-5 h-5 text-green-400" />
                Share Your Card
              </CardTitle>
              <CardDescription className="text-green-200/70">
                Image auto-downloads + app opens ready to share
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* SMS - Best Results */}
              <Button
                onClick={shareViaSMS}
                disabled={generating}
                className="w-full justify-between bg-green-600 hover:bg-green-700 text-white h-12"
                data-testid="share-sms-btn"
              >
                <div className="flex items-center">
                  {generating ? <Loader2 className="w-5 h-5 mr-3 animate-spin" /> : <MessageSquare className="w-5 h-5 mr-3" />}
                  Send via Text/SMS
                </div>
                <Badge className="bg-green-400/20 text-green-200 text-xs">Best Results</Badge>
              </Button>

              {/* Email */}
              <Button
                onClick={shareViaEmail}
                disabled={generating}
                className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white h-11"
                data-testid="share-email-btn"
              >
                {generating ? <Loader2 className="w-5 h-5 mr-3 animate-spin" /> : <Mail className="w-5 h-5 mr-3" />}
                Send via Email
              </Button>

              {/* Social Grid */}
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => shareWithImage("instagram")}
                  disabled={generating}
                  className="justify-center bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:opacity-90 text-white"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Instagram className="w-4 h-4 mr-2" />}
                  Instagram
                </Button>
                <Button
                  onClick={() => shareWithImage("facebook")}
                  disabled={generating}
                  className="justify-center bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Facebook className="w-4 h-4 mr-2" />}
                  Facebook
                </Button>
                <Button
                  onClick={() => shareWithImage("twitter")}
                  disabled={generating}
                  className="justify-center bg-black hover:bg-stone-900 text-white border border-stone-700"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Twitter className="w-4 h-4 mr-2" />}
                  X (Twitter)
                </Button>
                <Button
                  onClick={() => shareWithImage("native")}
                  disabled={generating}
                  className="justify-center bg-stone-700 hover:bg-stone-600 text-white"
                >
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                  More...
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Download Section */}
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-amber-400" />
                Download Image
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={downloadCard}
                disabled={generating}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold"
              >
                {generating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Download Card Image</>
                )}
              </Button>
              <p className="text-stone-500 text-xs text-center">
                High-quality PNG ready for any platform
              </p>
            </CardContent>
          </Card>

          {/* Link Section */}
          <Card className="bg-stone-800/50 border-stone-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2">
                <Link2 className="w-4 h-4 text-amber-400" />
                Your Tracked Link
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Input
                  value={currentTemplate.ctaLink}
                  readOnly
                  className="bg-stone-900 border-stone-700 text-white text-xs font-mono"
                />
                <Button
                  onClick={copyLink}
                  size="sm"
                  className="bg-stone-700 hover:bg-stone-600 text-white flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <Card className="bg-stone-800/50 border-stone-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Performance</p>
                    <p className="text-stone-400 text-xs">Your reach</p>
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-white">{shareStats.shares}</p>
                    <p className="text-xs text-stone-400">Shares</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-400">{shareStats.clicks}</p>
                    <p className="text-xs text-stone-400">Clicks</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* SMS Preview */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-amber-400" />
            Message Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md mx-auto">
            <div className="bg-green-600 text-white rounded-2xl rounded-br-sm p-4 shadow-lg">
              <p className="text-sm leading-relaxed">{currentTemplate.smsMessage}</p>
            </div>
            <p className="text-stone-500 text-xs mt-3 text-center">
              📱 Image auto-downloads when you tap share
            </p>
          </div>
        </CardContent>
      </Card>

      {/* NEW: Professional Social Media Cards Section */}
      <div className="border-t border-stone-700 pt-8">
        <SocialMediaCards 
          userId={user?.user_id}
          userName={userData.name}
          referralCode={user?.user_id}
          isSoldier={false}
        />
      </div>
    </div>
  );
};

export default DigitalMarketingToolkit;
